import { useState } from 'react';
import { Button } from 'components/Button';
import { ButtonGroup } from 'components/ButtonGroup';
import { Confirm } from 'components/confirm/Confirm';
import { DateTime } from 'components/DateTime';
import { FormInput } from 'components/form/FormInput';
import { FormSelect } from 'components/form/FormSelect';
import { useModal } from 'components/modal/ModalStack';
import { toMoney } from 'components/Money';
import { Text } from 'components/Text';
import { formatPercent } from 'helpers/format';
import { buildPaymentSplit, hasOpenPaymentData, isPendingUpsellPayment, paymentPercent, resolvePaymentDueDate } from 'helpers/DealPaymentSchedule';
import { React } from 'libs/react';
import './DealPaymentSplitModal.scss';
const SHARES = {
    '1': [100],
    '2': [50, 50],
    '3': [1, 1, 1],
    '4': [1, 1, 1, 1],
    '30-70': [30, 70]
};
/**
 * Content presented in the modal stack (`style: 'dialog'`). Fills the whole
 * schedule at once instead of one line at a time.
 *
 * @component DealPaymentSplitModal
 * @since 1.0.0
 */
export function DealPaymentSplitModal(props) {
    var _a;
    const { deal, hasDeposit, onApply, total } = props;
    const modal = useModal();
    const [split, setSplit] = useState('2');
    const [rule, setRule] = useState('NONE');
    const [days, setDays] = useState('30');
    const [deposit, setDeposit] = useState(true);
    const payments = (_a = deal === null || deal === void 0 ? void 0 : deal.payments) !== null && _a !== void 0 ? _a : [];
    const shares = SHARES[split];
    const canBeDeposit = hasDeposit === true &&
        !(deal === null || deal === void 0 ? void 0 : deal.directSale) &&
        shares.length > 1 &&
        !payments.some((payment) => payment.status === 'COMPLETED');
    const due = resolvePaymentDueDate(rule, Math.max(0, Math.floor(Number(days) || 0)), deal === null || deal === void 0 ? void 0 : deal.plannedDateStart);
    const result = buildPaymentSplit({
        deposit: canBeDeposit && deposit,
        dueDate: due.date,
        language: deal === null || deal === void 0 ? void 0 : deal.language,
        payments,
        shares,
        total
    });
    const lines = (result !== null && result !== void 0 ? result : []).filter((line) => line.status !== 'COMPLETED' && !isPendingUpsellPayment(line));
    const ruleOptions = [
        { value: 'NONE', label: t('To be confirmed') },
        ...((deal === null || deal === void 0 ? void 0 : deal.plannedDateStart)
            ? [{ value: 'BEFORE_EVENT', label: t('Days before the event') }]
            : []),
        { value: 'FROM_TODAY', label: t('Days from today') }
    ];
    /**
     * @function apply
     * @since 1.0.0
     * @hidden
     */
    const apply = () => {
        if (result) {
            modal.dismiss();
            onApply(result);
        }
    };
    /**
     * @function onApplyPress
     * @since 1.0.0
     * @hidden
     */
    const onApplyPress = () => {
        if (!hasOpenPaymentData(payments)) {
            apply();
            return;
        }
        modal.present({
            style: 'dialog',
            title: t('Confirm'),
            picto: 'triangle-exclamation',
            modal: (React.createElement(Confirm, { title: t('Replace existing payments?'), infos: t('The open payments will be replaced. Completed payments and upsells are kept.'), acceptText: t('Replace'), onAccept: apply }))
        });
    };
    return (React.createElement("div", { className: "deal-payment-split" },
        React.createElement("div", { className: "deal-payment-split-body" },
            React.createElement(FormSelect, { name: "split", size: "small", label: t('Payment split'), value: split, options: [
                    { value: '1', label: t('1 payment') },
                    { value: '2', label: t('2 payments (50/50)') },
                    { value: '30-70', label: t('2 payments (30/70)') },
                    { value: '3', label: t('3 equal payments') },
                    { value: '4', label: t('4 equal payments') }
                ], required: true, skipSort: true, onChange: setSplit }),
            React.createElement("div", { className: "deal-payment-split-due" },
                React.createElement(FormSelect, { name: "rule", size: "small", label: t('Last payment date'), value: rule, options: ruleOptions, required: true, skipSort: true, onChange: (value) => setRule(value) }),
                rule !== 'NONE' && (React.createElement(FormInput, { type: "number", size: "small", min: "0", label: t('Number of days'), value: days, onChange: setDays }))),
            canBeDeposit && (React.createElement("label", { className: "deal-payment-split-deposit" },
                React.createElement("input", { type: "checkbox", checked: deposit, onChange: () => setDeposit(!deposit) }),
                React.createElement("span", null,
                    React.createElement(Text, { value: t('The first payment is a deposit') }),
                    React.createElement(Text, { className: "deal-payment-split-notice", value: t('The client pays this amount when signing.') })))),
            result ? (React.createElement("div", { className: "deal-payment-split-preview" }, lines.map((line, index) => (React.createElement("div", { className: "deal-payment-split-line", key: index },
                React.createElement("div", { className: "deal-payment-split-line-date" }, line.isDeposit ? (React.createElement(Text, { value: t('At signing') })) : line.date ? (React.createElement(DateTime, { value: line.date, y: "numeric", m: "numeric", d: "numeric" })) : (React.createElement(Text, { value: t('To be confirmed') }))),
                React.createElement(Text, { className: "deal-payment-split-line-description", value: line.description }),
                React.createElement(Text, { value: formatPercent(paymentPercent(line, total), 2) }),
                React.createElement(Text, { className: "deal-payment-split-line-amount", value: toMoney(line.amount) })))))) : (React.createElement(Text, { className: "deal-payment-split-notice", value: t('Nothing is left to schedule: the payments already cover the total.') })),
            result && result.length > lines.length && (React.createElement(Text, { className: "deal-payment-split-notice", value: t('Completed payments and upsells are kept.') })),
            due.clamped && (React.createElement(Text, { className: "deal-payment-split-notice", value: t('The date falls in the past: it was brought back to today.') }))),
        React.createElement("div", { className: "deal-payment-split-foot" },
            React.createElement(ButtonGroup, null,
                React.createElement(Button, { size: "small", label: t('Cancel'), onPress: () => modal.dismiss() }),
                React.createElement(Button, { size: "small", style: "primary", label: t('Apply'), disabled: !result, onPress: onApplyPress })))));
}
